import { useEffect } from 'react'
import type { Action, History, Location } from 'history'

import { replace, useOptionalAppHistory } from 'lib/routing/appRouting'
import { useAppDispatch } from 'hooks/reduxHooks'
import type { AppDispatch } from 'src/client/store'
import { checkoutUrlPattern } from 'constants/url'
import { trackCheckoutObservabilityEvent } from 'analytics/snowplow/checkoutAnalytics'

let historyLocations: Array<Location> = []
let pendingReplace: Location | undefined

export function getCheckoutDepth() {
  return historyLocations.length
}

export function isCheckoutJourneyPath(pathname: string): boolean {
  return checkoutUrlPattern.test(pathname) || /^\/[a-z]{2}\/checkout-(restore-cart|gateway)/.test(pathname)
}

export function shouldTrackCheckoutExit(fromPathname: string | undefined, toPathname: string): boolean {
  return !!fromPathname && isCheckoutJourneyPath(fromPathname) && !isCheckoutJourneyPath(toPathname)
}

export function useCheckoutRouteTracker() {
  const dispatch = useAppDispatch()
  const history = useOptionalAppHistory()

  useEffect(() => {
    if (!history) return undefined

    return startRouteTracking(history, dispatch)
  }, [dispatch, history])
}

export function startRouteTracking(history: History, dispatch: AppDispatch) {
  try {
    if (history.action === 'POP') {
      restoreTracking(history)
    } else {
      historyLocations = [history.location]
    }
    return trackAndAdjustHistory(history, dispatch)
  } catch(e) {
    console.debug('route tracking is disabled', e)
    historyLocations = []
  }
}

function restoreTracking(history: History) {
  const lastTrackableLocation = historyLocations[historyLocations.length - 2]
  if (lastTrackableLocation && history.location.pathname === lastTrackableLocation.pathname) {
    historyLocations.splice(historyLocations.length - 1)
  } else {
    logHistoryBackError(history.location)
    throw new Error('restore tracking failed')
  }
}

let unlisten: (() => void) | undefined

function trackAndAdjustHistory(history: History, dispatch: AppDispatch): () => void {
  unlisten = history.listen(({ location, action }) => {
    handleLocationChange(history, dispatch, location, action)
  })
  return stopTracking
}

function handleLocationChange(history: History, dispatch: AppDispatch, location: Location, action: Action) {
  const previousPathname = historyLocations.at(-1)?.pathname
  if (shouldTrackCheckoutExit(previousPathname, location.pathname)) {
    trackCheckoutObservabilityEvent({
      subject: 'attempt',
      action: 'exited_checkout',
      type: 'operational',
    })
  }

  const idxInHistory = historyLocations.findIndex(item => item.pathname == location.pathname)
  switch (action) {
    case 'POP':
      if (idxInHistory === -1) {
        logHistoryBackError(location)
        stopTracking()
      }
      historyLocations.splice(idxInHistory + 1)
      break
    case 'PUSH':
      if (idxInHistory !== -1) {
        // the target page already exists in the history, so we will go back
        // to where it was and do an update/REPLACE there after the going back is finished
        goBack(history, idxInHistory)
        pendingReplace = location
      } else {
        historyLocations.push(location)
      }
      break
    case 'REPLACE':
      historyLocations.splice(-1)
      historyLocations.push(location)
      break
  }

  // do the REPLACE after the history going back is finished
  // to update the page in the history
  if (action == 'POP' && pendingReplace) {
    dispatch(replace(pendingReplace))
    pendingReplace = undefined
  }
}

function stopTracking() {
  if (unlisten) {
    unlisten()
    unlisten = undefined
  }
}

function goBack(history: History, idxInHistory: number) {
  const upSteps = idxInHistory - historyLocations.length
  // -1 means the previous page, so no splice is needed for history
  if (upSteps !== -1) {
    historyLocations.splice(upSteps + 1)
  }
  history.go(upSteps)
}

function logHistoryBackError(location: Location) {
  console.debug(`unrecognized history back to ${location.pathname}`)
  console.debug('history', JSON.stringify(historyLocations))
}
